import Web3 from "web3";
import CVTVestingABI from "../abis/VestingV2.json";
import ERC20ABI from "../abis/SimpleERC20.json";
import AutomationRegistryABI from "../abis/AutomationRegistry.json";
import PancakeRouterABI from "../abis/PancakeRouter.json";

interface VestingInfo {
  depositAmount: string;
  claimedAmount: string;
  startTime: number;
  initiated: boolean;
  claimable: string;
  fee: string;
}
// Interfaces remain the same
export interface UserDetails {
  vestedToken: string;
  startTime: number;
  tokenReleased: string;
  claimableAmount: string;
  feesDeposited: boolean;
  claimTriggered: boolean;
  onHold: boolean;
  adminApproved: boolean;
  vestingStarted: boolean;
  firstClaimDone: boolean;
  feesTobePaid: string;
}

interface VestingContractResult {
  depositAmount: string;
  claimedAmount: string;
  startTime: string;
  initiated: boolean;
  claimable: string;
  fee: string;
}

export interface ContractStatus {
  paused: boolean;
  /** Percent strings, e.g. "1" for 1%. */
  initialPercent: string;
  weeklyPercent: string;
  /** Exact decimal BNB string. */
  feesCollected: string;
  depositorCount: number;
  /** CVT V2 held by the vesting contract; null if the read failed. */
  v2Balance: string | null;
  /** CVT V1 deposited by users and held by the contract; what Withdraw CVT moves. */
  v1Balance: string | null;
  /** The CVT V2 token address, read from the contract. */
  releaseToken: string;
}

export interface AutomationWalletStatus {
  address: string;
  /** Exact decimal BNB string. */
  balance: string;
  /** True when the bot is close to running out of gas. */
  isLow: boolean;
}

export interface SwapQuote {
  /** Base units (wei), exact. */
  amountInRaw: string;
  amountOutRaw: string;
  /** Decimal peg-LINK string, exact. */
  amountOut: string;
}

export interface UpkeepStatus {
  upkeepId: string;
  /** Base units (wei), exact. */
  balanceRaw: string;
  minBalanceRaw: string;
  /** Decimal LINK strings, exact. */
  balance: string;
  minBalance: string;
  /** True when the registry would stop performing this upkeep. */
  isUnderfunded: boolean;
}

// Environment variable checks remain the same
const CVT_V1_TOKEN_ADDRESS = process.env.REACT_APP_CVT1_TOKEN_ADDRESS;
const CVT_VESTING_CONTRACT_ADDRESS =
  process.env.REACT_APP_VESTING_CONTRACT_ADDRESS;

// Add a check to ensure variables are loaded, which helps debugging.
if (!CVT_V1_TOKEN_ADDRESS || !CVT_VESTING_CONTRACT_ADDRESS) {
  throw new Error(
    "CRITICAL: Contract addresses are not defined. Check your .env file and ensure it's loaded correctly."
  );
}

// Target chain. Defaults to BSC Mainnet; set REACT_APP_CHAIN_ID=0x61 in .env
// to point the whole app at BSC Testnet alongside the testnet addresses.
// Contracts only exist on the configured chain — calls elsewhere return empty
// data that would otherwise be displayed as a zero balance.
export const BSC_CHAIN_ID = (
  process.env.REACT_APP_CHAIN_ID || "0x38"
).toLowerCase();

const IS_TESTNET = BSC_CHAIN_ID === "0x61";

export const BSC_CHAIN_PARAMS = IS_TESTNET
  ? {
      chainId: BSC_CHAIN_ID,
      chainName: "BNB Smart Chain Testnet",
      nativeCurrency: { name: "tBNB", symbol: "tBNB", decimals: 18 },
      rpcUrls: ["https://bsc-testnet-rpc.publicnode.com"],
      blockExplorerUrls: ["https://testnet.bscscan.com/"],
    }
  : {
      chainId: BSC_CHAIN_ID,
      chainName: "BNB Smart Chain",
      nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
      rpcUrls: ["https://bsc-dataseed.bnbchain.org/"],
      blockExplorerUrls: ["https://bscscan.com/"],
    };

export const NETWORK_LABEL = IS_TESTNET ? "BSC Testnet" : "BSC Mainnet";

/** Block explorer root, no trailing slash — for building address links. */
export const EXPLORER_URL = BSC_CHAIN_PARAMS.blockExplorerUrls[0].replace(
  /\/+$/,
  ""
);

/** The vesting contract, re-exported so the UI can link to it. */
export const VESTING_ADDRESS = CVT_VESTING_CONTRACT_ADDRESS as string;

/**
 * Chainlink Automation registry + LINK token, per network.
 * Verified on-chain 2026-08-07: both registries report "KeeperRegistry 2.1.0".
 *
 * NOTE: Automation v2.1 is past its announced sunset (mainnet 2026-07-31,
 * testnet 2026-06-24) and Chainlink now directs users to CRE. These reads work
 * today; the funding path will need revisiting after CRE migration.
 */
export const AUTOMATION_REGISTRY_ADDRESS = IS_TESTNET
  ? "0x96bb60aAAec09A0FceB4527b81bbF3Cc0c171393"
  : "0xDc21E279934fF6721CaDfDD112DAfb3261f09A2C";

export const LINK_TOKEN_ADDRESS = IS_TESTNET
  ? "0x84b9B910527Ad5C03A9Ca831909E21e236EA7b06"
  : "0x404460C6A5EdE2D891e8297795264fDe62ADBB75";

/**
 * Multicall3 — same address on every EVM chain including BNB. Used to batch
 * hundreds of read calls into a handful of round-trips.
 */
const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";
const MULTICALL3_ABI = [
  {
    inputs: [
      {
        components: [
          { name: "target", type: "address" },
          { name: "allowFailure", type: "bool" },
          { name: "callData", type: "bytes" },
        ],
        name: "calls",
        type: "tuple[]",
      },
    ],
    name: "aggregate3",
    outputs: [
      {
        components: [
          { name: "success", type: "bool" },
          { name: "returnData", type: "bytes" },
        ],
        name: "returnData",
        type: "tuple[]",
      },
    ],
    stateMutability: "payable",
    type: "function",
  },
];

/**
 * Wallet used by the off-chain automation bot (AWS) that calls performUpkeep.
 *
 * It pays its own BNB gas, so it must stay funded or automation stalls. The
 * admin console shows this balance and can top it up.
 */
export const AUTOMATION_WALLET =
  process.env.REACT_APP_AUTOMATION_WALLET ||
  "0xE9c74643617807A1DD07AC45828Ab6ba01Ac4B25";

/** Below this the bot is at risk of stalling; the UI warns. */
export const AUTOMATION_LOW_BNB = "0.01";

/** Numeric upkeep id from automation.chain.link. Empty until configured. */
export const UPKEEP_ID = process.env.REACT_APP_UPKEEP_ID || "";

/**
 * PancakeSwap V2 router + Binance-Peg LINK, for the BNB -> peg-LINK swap.
 *
 * IMPORTANT: peg-LINK is NOT the token Automation accepts. This swap only
 * covers the first leg; the user must then convert peg-LINK to native ERC-677
 * LINK via PegSwap (https://pegswap.chain.link/) before funding an upkeep.
 * Verified 2026-08-08 on mainnet: 1 BNB ~ 71.77 peg-LINK, <0.3% price impact.
 *
 * Mainnet only — BSC testnet has no meaningful BNB/LINK liquidity, so the
 * swap UI is hidden there rather than offering a route that would revert.
 */
export const SWAP_SUPPORTED = !IS_TESTNET;
export const PANCAKE_ROUTER_ADDRESS =
  "0x10ED43C718714eb63d5aA57B78B54704E256024E";
export const WBNB_ADDRESS = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
export const PEG_LINK_ADDRESS = "0xF8A0BF9cF54Bb92F17374d9e9A321E6a111a51bD";
export const PEGSWAP_URL = "https://pegswap.chain.link/";

// Thrown when the wallet is connected to a chain other than BSC Mainnet.
// Callers use this to distinguish "wrong network" from a genuine RPC failure.
export class WrongNetworkError extends Error {
  public readonly currentChainId: string;
  constructor(currentChainId: string) {
    super("Please switch your wallet to the BNB Smart Chain (BSC) network.");
    this.name = "WrongNetworkError";
    this.currentChainId = currentChainId;
  }
}

/**
 * Sentinel meaning "deposit the entire balance". Passing this instead of a
 * formatted number lets depositCVT use the on-chain base-units figure directly,
 * so a max deposit never fails on a rounding mismatch.
 */
export const MAX_BALANCE = "__MAX_BALANCE__";

/**
 * Exact base-units -> decimal string. No floats anywhere.
 *
 * Number() loses precision above 2^53, so an 18-decimal balance like
 * 999999999999999999 would render as "1" — a whole token more than the user
 * actually holds. All display values must route through here.
 */
export function formatUnits(rawAmount: string, decimals: number): string {
  if (!rawAmount) return "0";
  const negative = rawAmount.startsWith("-");
  const raw = BigInt(negative ? rawAmount.slice(1) : rawAmount);
  const divisor = BigInt(10) ** BigInt(decimals);
  const whole = raw / divisor;
  const frac = (raw % divisor).toString().padStart(decimals, "0");
  const trimmed = frac.replace(/0+$/, "");
  const sign = negative ? "-" : "";
  return trimmed ? `${sign}${whole}.${trimmed}` : `${sign}${whole}`;
}

/**
 * Exact decimal string -> base units. Replaces web3.utils.toWei, which
 * hardcodes 18 decimals and throws on inputs with more precision than that.
 */
export function parseUnits(value: string, decimals: number): string {
  if (!value) return "0";
  const negative = value.startsWith("-");
  const clean = negative ? value.slice(1) : value;
  const [wholePart = "0", fracPart = ""] = clean.split(".");
  if (fracPart.length > decimals) {
    throw new Error(
      `Value ${value} has more than ${decimals} decimal places.`
    );
  }
  const padded = fracPart.padEnd(decimals, "0");
  const combined = `${wholePart}${padded}`.replace(/^0+(?=\d)/, "");
  return `${negative ? "-" : ""}${combined}`;
}

/** True when a string is a plain decimal number this module can parse exactly. */
export function isDecimalString(value: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(value.trim());
}

/**
 * Exact comparison of two decimal strings; returns -1, 0 or 1.
 * Sorting via parseFloat collapses 18-decimal values that differ only past
 * ~15 significant digits, so distinct balances would compare as equal.
 */
export function compareDecimals(a: string, b: string, decimals = 18): number {
  const bigA = BigInt(parseUnits(a.trim(), decimals));
  const bigB = BigInt(parseUnits(b.trim(), decimals));
  if (bigA < bigB) return -1;
  if (bigA > bigB) return 1;
  return 0;
}

/**
 * Exact subtraction of two decimal strings. Floats cannot represent most
 * 18-decimal values, so a - b drifts; this scales to integers first.
 */
export function subtractDecimals(a: string, b: string, decimals = 18): string {
  const diff = BigInt(parseUnits(a || "0", decimals)) -
    BigInt(parseUnits(b || "0", decimals));
  return formatUnits(diff.toString(), decimals);
}

/**
 * Group the whole part with thousands separators, keeping the fractional part
 * exact and untouched.
 */
export function formatWithCommas(value: string, maxDecimals?: number): string {
  const shown =
    maxDecimals === undefined ? value : truncateDecimals(value, maxDecimals);
  const [whole, frac] = shown.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac ? `${grouped}.${frac}` : grouped;
}

/**
 * True when a decimal string represents any non-zero amount, however small.
 * parseFloat would underflow dust amounts like 0.000000000000000001 to 0.
 */
export function hasPositiveBalance(value: string | null): boolean {
  if (!value || value.startsWith("-")) return false;
  // Any non-zero digit in either the whole or fractional part.
  return /[1-9]/.test(value);
}

/**
 * Truncate (never round up) a decimal string for display. Rounding up would
 * show a balance the user cannot actually spend.
 */
export function truncateDecimals(value: string, places: number): string {
  const [whole, frac = ""] = value.split(".");
  if (places === 0) return whole;
  return `${whole}.${frac.padEnd(places, "0").slice(0, places)}`;
}

class Web3ServiceClass {
  private web3: Web3 | null = null;
  private currentAccount: string = "";
  private decimalsCache: Map<string, number> = new Map();

  private vestingContract: any = null;
  private cvtV1Contract: any = null;
  private tokenContractCache: Map<string, any> = new Map();

  public async init(): Promise<boolean> {
    // Guard clause: If already initialized, do nothing
    if (this.web3) return true;

    if (this.isMetaMaskAvailable()) {
      try {
        // Add a small delay to ensure provider is ready (important for mobile wallets)
        await new Promise((resolve) => setTimeout(resolve, 100));

        this.web3 = new Web3(window.ethereum);

        // Wait for provider to be connected
        let retries = 0;
        while (!window.ethereum.isConnected?.() && retries < 10) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          retries++;
        }

        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        });

        if (accounts && accounts.length > 0) {
          this.currentAccount = accounts[0];

          // Verify the wallet is on BSC before initializing contracts.
          // On any other chain the contract addresses hold no code, and every
          // call silently returns empty data instead of erroring.
          const chainId = await this.getChainId();
          console.log("Connected to chain:", chainId);
          if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
            throw new WrongNetworkError(chainId);
          }

          this.initializeContracts();

          // Verify contracts are accessible
          try {
            await this.cvtV1Contract.methods.decimals().call();
          } catch (error) {
            console.error("Contract verification failed, reinitializing...");
            this.initializeContracts();
          }

          return true;
        } else {
          throw new Error("Permission to access accounts was denied.");
        }
      } catch (error) {
        console.error("Failed to initialize Web3 Service:", error);
        throw error;
      }
    } else {
      throw new Error("MetaMask or a compatible wallet is not available.");
    }
  }

  /**
   * Live contract state for the admin status bar. Surfacing this inline means
   * the admin can see what they are replacing instead of typing into blank
   * fields with no idea of the current configuration.
   */
  async getContractStatus(): Promise<ContractStatus> {
    if (!this.web3 || !this.vestingContract) {
      await this.init();
      if (!this.vestingContract) throw new Error("Web3 not initialized");
    }
    const [paused, params, fees, depositors, releaseToken] = await Promise.all([
      this.vestingContract.methods.paused().call(),
      this.vestingContract.methods.getGlobalVestingParams().call(),
      this.vestingContract.methods.totalFeesCollected().call(),
      this.vestingContract.methods.depositorCount().call(),
      this.vestingContract.methods.releaseToken().call(),
    ]);

    // CVT V2 held by the contract — this is the float available to pay out
    // claims, so an admin needs to see it without leaving the page.
    let v2Balance: string | null = null;
    try {
      const token = this.getTokenContract(releaseToken as string);
      const [raw, dec] = await Promise.all([
        token.methods.balanceOf(CVT_VESTING_CONTRACT_ADDRESS).call(),
        this.getDecimals(releaseToken as string),
      ]);
      v2Balance = formatUnits(raw.toString(), dec);
    } catch (error) {
      console.error("[getContractStatus] could not read CVT V2 balance", error);
    }

    // CVT V1 deposited by users and still sitting in the contract — this is
    // exactly what "Withdraw CVT" moves, so the card can show the amount.
    let v1Balance: string | null = null;
    try {
      const raw = (await this.cvtV1Contract.methods
        .balanceOf(CVT_VESTING_CONTRACT_ADDRESS)
        .call()) as string;
      v1Balance = formatUnits(
        raw.toString(),
        await this.getDecimals(CVT_V1_TOKEN_ADDRESS!)
      );
    } catch (error) {
      console.error("[getContractStatus] could not read CVT V1 balance", error);
    }
    // Percentages are stored scaled by 100 (10000 = 100%).
    const scaled = (v: any) => (Number(v) / 100).toString();
    return {
      paused: Boolean(paused),
      initialPercent: scaled(params[0] ?? params["0"]),
      weeklyPercent: scaled(params[1] ?? params["1"]),
      feesCollected: formatUnits(fees.toString(), 18),
      depositorCount: Number(depositors),
      v2Balance,
      v1Balance,
      releaseToken: releaseToken as string,
    };
  }

  /**
   * Total claimable across every eligible depositor.
   *
   * The contract has no aggregate getter, so this scans all depositors via
   * Multicall3 and sums them. It costs a few seconds for ~500 users, which is
   * why the admin strip loads it on demand rather than on mount.
   */
  async getTotalClaimable(): Promise<{ total: string; eligible: number }> {
    if (!this.web3 || !this.vestingContract) {
      await this.init();
      if (!this.vestingContract) throw new Error("Web3 not initialized");
    }

    const count = Number(
      await this.vestingContract.methods.depositorCount().call()
    );
    if (!count) return { total: "0", eligible: 0 };

    const multicall = new this.web3!.eth.Contract(
      MULTICALL3_ABI as any,
      MULTICALL3_ADDRESS
    );
    const vesting = this.vestingContract;

    const BATCH = 50;
    const batches = Math.ceil(count / BATCH);

    // Batches are independent, so run them together rather than in sequence.
    const parts = await Promise.all(
      Array.from({ length: batches }, async (_, b) => {
        const start = b * BATCH;
        const end = Math.min(start + BATCH - 1, count - 1);
        try {
          const addrCalls = [];
          for (let i = start; i <= end; i++) {
            addrCalls.push({
              target: CVT_VESTING_CONTRACT_ADDRESS,
              allowFailure: true,
              callData: vesting.methods.getDepositor(i).encodeABI(),
            });
          }
          const addrRes: any[] = await multicall.methods
            .aggregate3(addrCalls)
            .call();

          const addresses: string[] = [];
          for (const r of addrRes) {
            const ok = r.success ?? r[0];
            const data = r.returnData ?? r[1];
            if (!ok || !data || data === "0x") continue;
            addresses.push(
              this.web3!.eth.abi.decodeParameter("address", data) as string
            );
          }
          if (!addresses.length) return { total: 0n, eligible: 0 };

          const detailCalls = addresses.flatMap((a) => [
            {
              target: CVT_VESTING_CONTRACT_ADDRESS,
              allowFailure: true,
              callData: vesting.methods.getUserDetails(a).encodeABI(),
            },
            {
              target: CVT_VESTING_CONTRACT_ADDRESS,
              allowFailure: true,
              callData: vesting.methods.blockedUsers(a).encodeABI(),
            },
          ]);
          const detailRes: any[] = await multicall.methods
            .aggregate3(detailCalls)
            .call();

          let total = 0n;
          let eligible = 0;
          for (let i = 0; i < addresses.length; i++) {
            const d = detailRes[i * 2];
            const bl = detailRes[i * 2 + 1];
            const dOk = d?.success ?? d?.[0];
            const bOk = bl?.success ?? bl?.[0];
            if (!dOk || !bOk) continue;

            const details: any = this.web3!.eth.abi.decodeParameters(
              [
                "uint256", "uint256", "uint256", "uint256", "uint256",
                "bool", "bool", "bool", "bool", "bool", "bool",
              ],
              d.returnData ?? d[1]
            );
            const blocked = this.web3!.eth.abi.decodeParameter(
              "bool",
              bl.returnData ?? bl[1]
            ) as unknown as boolean;

            const claimable = BigInt(details[3]);
            const feesDeposited = Boolean(details[5]);
            const claimTriggered = Boolean(details[6]);
            const onHold = Boolean(details[7]);
            const adminApproved = Boolean(details[8]);
            const vestingStarted = Boolean(details[9]);

            // Mirrors the contract's own eligibility rule.
            if (
              feesDeposited &&
              !claimTriggered &&
              vestingStarted &&
              !blocked &&
              (!onHold || adminApproved) &&
              claimable > 0n
            ) {
              total += claimable;
              eligible++;
            }
          }
          return { total, eligible };
        } catch (error) {
          console.error(`[getTotalClaimable] batch ${b} failed`, error);
          return { total: 0n, eligible: 0 };
        }
      })
    );

    const sum = parts.reduce((a, p) => a + p.total, 0n);
    const eligible = parts.reduce((a, p) => a + p.eligible, 0);
    return { total: formatUnits(sum.toString(), 18), eligible };
  }

  /** Current wallet chain id, e.g. "0x38" for BSC Mainnet. */
  public async getChainId(): Promise<string> {
    if (!this.isMetaMaskAvailable()) {
      throw new Error("MetaMask or a compatible wallet is not available.");
    }
    return (await window.ethereum.request({
      method: "eth_chainId",
    })) as string;
  }

  public async isOnBSC(): Promise<boolean> {
    try {
      return (await this.getChainId()).toLowerCase() === BSC_CHAIN_ID;
    } catch {
      return false;
    }
  }

  /**
   * Ask the wallet to switch to BSC, adding the network first if it is unknown.
   * Resolves true once the wallet reports BSC as the active chain.
   */
  public async switchToBSC(): Promise<boolean> {
    if (!this.isMetaMaskAvailable()) {
      throw new Error("MetaMask or a compatible wallet is not available.");
    }

    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: BSC_CHAIN_ID }],
      });
    } catch (error: any) {
      // 4902 = chain not added to the wallet yet. Add it, which also switches.
      // Some mobile wallets report this as -32603 or omit the code entirely.
      const code = error?.code ?? error?.data?.originalError?.code;
      if (code === 4902 || code === -32603) {
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [BSC_CHAIN_PARAMS],
          });
        } catch (addError) {
          console.error("Failed to add BSC network:", addError);
          return false;
        }
      } else if (code === 4001) {
        // User rejected the switch.
        return false;
      } else {
        console.error("Failed to switch network:", error);
        return false;
      }
    }

    // Re-read rather than trusting the call to have resolved post-switch;
    // some wallets resolve before the chain actually changes.
    const onBSC = await this.isOnBSC();
    if (onBSC) {
      // Rebind contracts to the now-correct chain.
      this.web3 = new Web3(window.ethereum);
      this.decimalsCache.clear();
      this.tokenContractCache.clear();
      this.initializeContracts();
    }
    return onBSC;
  }

  /** Token decimals, cached — this never changes for a given token. */
  private async getDecimals(tokenAddress: string): Promise<number> {
    const key = tokenAddress.toLowerCase();
    const cached = this.decimalsCache.get(key);
    if (cached !== undefined) return cached;

    const tokenContract = this.getTokenContract(tokenAddress);
    const raw = (await tokenContract.methods.decimals().call()) as string;
    const decimals = Number(raw);
    if (!Number.isFinite(decimals)) {
      throw new Error(`Token ${tokenAddress} returned invalid decimals: ${raw}`);
    }
    this.decimalsCache.set(key, decimals);
    return decimals;
  }

  /**
   * Full-precision balance as a decimal string, or null if it could not be
   * read. Callers truncate for display; the exact value is preserved here so
   * deposits can use it without rounding drift.
   *
   * Returning null rather than "0.00" is deliberate: a failed RPC call and a
   * genuinely empty wallet are different states, and showing 0 for the former
   * misleads users who do hold tokens.
   *
   * Throws WrongNetworkError when the wallet is off BSC, so the UI can prompt
   * to switch instead of reporting a load failure.
   */
  async getBalance(
    walletAddress: string,
    tokenAddress: string
  ): Promise<string | null> {
    if (!this.web3) {
      // Try to reinitialize if web3 is not available
      await this.init();
      if (!this.web3)
        throw new Error("Web3 not initialized. Please connect your wallet.");
    }

    // Diagnostics are deliberately always-on, including in production: a wrong
    // balance is silent and unreproducible without knowing which condition hit.
    const diag = (stage: string, extra: Record<string, unknown> = {}) =>
      console.log("[getBalance]", stage, {
        wallet: walletAddress,
        token: tokenAddress,
        ...extra,
      });

    // A wrong chain looks identical to an empty wallet at the RPC layer, so
    // check it up front and surface it as its own condition.
    const chainId = await this.getChainId();
    if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
      console.error("[getBalance] FAIL: wrong network", {
        walletChainId: chainId,
        expectedChainId: BSC_CHAIN_ID,
        expectedNetwork: NETWORK_LABEL,
      });
      throw new WrongNetworkError(chainId);
    }

    if (!this.web3.utils.isAddress(walletAddress)) {
      console.error("[getBalance] FAIL: wallet address is not a valid address", {
        wallet: walletAddress,
      });
      return null;
    }
    if (!this.web3.utils.isAddress(tokenAddress)) {
      console.error("[getBalance] FAIL: token address is not a valid address", {
        token: tokenAddress,
        hint: "check REACT_APP_CVT1_TOKEN_ADDRESS in the deployed build",
      });
      return null;
    }

    // A token address with no contract code returns "0x" for every call, which
    // web3 surfaces as an empty/zero result rather than an error. That looks
    // exactly like a zero balance, so check it explicitly.
    try {
      const code = await this.web3.eth.getCode(tokenAddress);
      if (!code || code === "0x" || code === "0x0") {
        console.error("[getBalance] FAIL: no contract at token address", {
          token: tokenAddress,
          chainId,
          hint: "wrong token address for this network, or wrong network",
        });
        return null;
      }
    } catch (error) {
      console.warn("[getBalance] could not verify token code", error);
    }

    const tokenContract = this.getTokenContract(tokenAddress);

    // Retry for mobile wallets, whose injected providers drop calls under load.
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const decimals = await this.getDecimals(tokenAddress);
        const balance = (await tokenContract.methods
          .balanceOf(walletAddress)
          .call()) as string;

        if (balance === null || balance === undefined) {
          throw new Error("balanceOf returned no data");
        }

        // Full precision — the UI truncates for display, but the deposit path
        // needs the exact figure.
        const formatted = formatUnits(balance.toString(), decimals);
        diag("OK", {
          raw: balance.toString(),
          decimals,
          formatted,
          attempt: attempt + 1,
        });
        return formatted;
      } catch (error: any) {
        lastError = error;
        console.warn(`[getBalance] attempt ${attempt + 1}/3 failed`, {
          message: error?.message,
          code: error?.code,
        });
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    }

    console.error("[getBalance] FAIL: all 3 attempts failed", {
      wallet: walletAddress,
      token: tokenAddress,
      chainId,
      provider: this.describeProvider(),
      lastError,
    });
    return null;
  }

  /** Which injected wallet is in use — mobile providers fail differently. */
  private describeProvider(): string {
    const eth: any = (window as any).ethereum;
    if (!eth) return "none";
    const flags = [
      eth.isTrust && "Trust",
      eth.isMetaMask && "MetaMask",
      eth.isTokenPocket && "TokenPocket",
      eth.isCoinbaseWallet && "Coinbase",
    ].filter(Boolean);
    return flags.length ? flags.join("+") : "unknown";
  }

  private initializeContracts(): void {
    if (!this.web3) return;

    this.vestingContract = new this.web3.eth.Contract(
      CVTVestingABI.abi as any,
      CVT_VESTING_CONTRACT_ADDRESS
    );

    this.cvtV1Contract = new this.web3.eth.Contract(
      ERC20ABI.abi as any,
      CVT_V1_TOKEN_ADDRESS
    );

    this.tokenContractCache.clear();
  }

  isMetaMaskAvailable(): boolean {
    return (
      typeof window !== "undefined" && typeof window.ethereum !== "undefined"
    );
  }

  // --- CHANGE 2: SIMPLIFIED connectWallet to use the new init method ---
  /**
   * Connects to the user's wallet by calling the init method.
   * This is now the primary entry point for your application to connect.
   */
  public async connectWallet(): Promise<string> {
    try {
      await this.init(); // All logic is now in init()
      if (!this.currentAccount) {
        throw new Error("Wallet connected, but no account was found.");
      }
      return this.currentAccount;
    } catch (error) {
      console.error("connectWallet failed:", error);
      // Re-throw the error to be handled by the UI component
      throw error;
    }
  }

  private getTokenContract(tokenAddress: string): any {
    if (!this.web3) throw new Error("Web3 not initialized");

    if (this.tokenContractCache.has(tokenAddress)) {
      return this.tokenContractCache.get(tokenAddress);
    }

    const contract = new this.web3.eth.Contract(
      ERC20ABI.abi as any,
      tokenAddress
    );
    this.tokenContractCache.set(tokenAddress, contract);

    if (this.tokenContractCache.size > 100) {
      const firstKey: any = this.tokenContractCache.keys().next().value;
      this.tokenContractCache.delete(firstKey);
    }

    return contract;
  }

  async depositCVT(amount: string): Promise<{ success: boolean }> {
    if (
      !this.web3 ||
      !this.currentAccount ||
      !this.vestingContract ||
      !this.cvtV1Contract
    ) {
      throw new Error("Web3 not initialized or wallet not connected");
    }

    try {
      const decimals = await this.getDecimals(CVT_V1_TOKEN_ADDRESS!);

      // Get user's actual balance in base units
      const userBalanceRaw = (await this.cvtV1Contract.methods
        .balanceOf(this.currentAccount)
        .call()) as string;
      const userBalance = userBalanceRaw.toString();

      let amountInWei: string;

      // MAX_BALANCE means "deposit everything" — pass the on-chain figure
      // straight through so no decimal round-trip can introduce drift.
      if (amount === MAX_BALANCE) {
        amountInWei = userBalance;
      } else {
        // parseUnits honours the token's real decimals; toWei hardcodes 18.
        amountInWei = parseUnits(amount, decimals);
      }

      // Final safety check
      if (BigInt(amountInWei) > BigInt(userBalance)) {
        throw new Error(
          "Insufficient balance. You cannot deposit more than you own."
        );
      }

      // Approve the vesting contract
      const estimatedGasApprove = await this.cvtV1Contract.methods
        .approve(CVT_VESTING_CONTRACT_ADDRESS, amountInWei)
        .estimateGas({
          from: this.currentAccount,
        });

      const gasLimitApprove = (BigInt(estimatedGasApprove) * 120n) / 100n;

      await this.cvtV1Contract.methods
        .approve(CVT_VESTING_CONTRACT_ADDRESS, amountInWei)
        .send({
          from: this.currentAccount,
          gas: gasLimitApprove.toString(),
        });

      // Deposit tokens
      const estimatedGasDeposit = await this.vestingContract.methods
        .depositCVT(amountInWei)
        .estimateGas({
          from: this.currentAccount,
        });

      const gasLimitDeposit = (BigInt(estimatedGasDeposit) * 120n) / 100n;

      await this.vestingContract.methods.depositCVT(amountInWei).send({
        from: this.currentAccount,
        gas: gasLimitDeposit.toString(),
      });

      return { success: true };
    } catch (error) {
      console.error("Deposit error:", error);
      throw new Error(
        `Failed to deposit tokens: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  // Add this method to get exact balance in wei for max deposits
  async getExactBalance(tokenAddress: string): Promise<string> {
    if (!this.web3 || !this.currentAccount) {
      throw new Error("Web3 not initialized or wallet not connected");
    }

    try {
      const tokenContract = this.getTokenContract(tokenAddress);
      const balanceWei = (await tokenContract.methods
        .balanceOf(this.currentAccount)
        .call()) as string;
      const decimals = await this.getDecimals(tokenAddress);

      // Uses the token's real decimals rather than assuming 18.
      return formatUnits(balanceWei.toString(), decimals);
    } catch (error) {
      console.error("Error getting exact balance:", error);
      return "0";
    }
  }

  /** Raw base-units balance — no decimal conversion, no precision loss. */
  async getRawBalance(tokenAddress: string): Promise<string> {
    if (!this.web3 || !this.currentAccount) {
      throw new Error("Web3 not initialized or wallet not connected");
    }
    const tokenContract = this.getTokenContract(tokenAddress);
    const balanceWei = (await tokenContract.methods
      .balanceOf(this.currentAccount)
      .call()) as string;
    return balanceWei.toString();
  }
  async getUserDetails(walletAddress: string): Promise<UserDetails> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      const details: any = await this.vestingContract.methods
        .getUserDetails(walletAddress)
        .call();
      const feesTobePaid: any = await this.vestingContract.methods
        .getCurrentFeeForUser(walletAddress)
        .call();

      const decimalsNum = await this.getDecimals(CVT_V1_TOKEN_ADDRESS!);

      const convertAmount = (amount: string) => {
        if (!amount) return "0";
        return formatUnits(amount, decimalsNum);
      };

      return {
        vestedToken: convertAmount(details[0].toString()),
        startTime: Number(details[1]),
        tokenReleased: convertAmount(details[2].toString()),
        claimableAmount: convertAmount(details[3].toString()),
        feesDeposited: details[4],
        claimTriggered: details[5],
        onHold: details[6],
        adminApproved: details[7],
        vestingStarted: details[8],
        firstClaimDone: details[9],
        feesTobePaid: feesTobePaid.toString(),
      };
    } catch (error) {
      console.error("Error getting user details:", error);
      throw error;
    }
  }

  async payFee(): Promise<{ success: boolean; txHash: string }> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract) {
      throw new Error("Web3 not initialized or wallet not connected");
    }

    try {
      const requiredFeeWei = await this.vestingContract.methods
        .getCurrentFeeForUser(this.currentAccount)
        .call();

      const feeWithBuffer = (BigInt(requiredFeeWei as any) * 101n) / 100n;
      const tx = await this.vestingContract.methods.payFee().send({
        from: this.currentAccount,
        value: feeWithBuffer.toString(),
      });

      return { success: true, txHash: tx.transactionHash };
    } catch (error) {
      console.error("Fee payment error:", error);
      throw error;
    }
  }

  async claimTokens(): Promise<{ success: boolean; txHash: string }> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract) {
      throw new Error("Web3 not initialized or wallet not connected");
    }

    try {
      let gasEstimate;
      try {
        gasEstimate = await this.vestingContract.methods
          .claimTokens()
          .estimateGas({
            from: this.currentAccount,
          });
      } catch (estimateError: any) {
        console.error("Gas estimation failed:", estimateError);
        if (estimateError.message) {
          throw new Error(estimateError.message);
        }
        throw new Error(
          "Transaction will fail. Please check your eligibility."
        );
      }

      const tx = await this.vestingContract.methods.claimTokens().send({
        from: this.currentAccount,
        gas: Math.floor(Number(gasEstimate) * 1.2).toString(),
      });
      return { success: true, txHash: tx.transactionHash };
    } catch (error: any) {
      console.error("Claim error:", error);

      let errorMessage = "Claim transaction failed";
      if (error.message) {
        const revertMatch = error.message.match(
          /reverted with reason string '(.*)'/
        );
        if (revertMatch) {
          errorMessage = revertMatch[1];
        } else {
          errorMessage = error.message;
        }
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Get user's vesting parameters (initial and weekly percentages)
   * Shows whether user has custom parameters or uses global defaults
   * Percentages are in basis points (e.g., 2000 = 20%)
   */
  async getVestingParams(walletAddress: string): Promise<{
    initialPercent: number;
    weeklyPercent: number;
    hasCustomParams: boolean;
  }> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      const params: any = await this.vestingContract.methods
        .getVestingParams(walletAddress)
        .call();

      return {
        initialPercent: Number(params[0]),
        weeklyPercent: Number(params[1]),
        hasCustomParams: params[2],
      };
    } catch (error) {
      console.error("Error getting vesting params:", error);
      throw error;
    }
  }

  /**
   * Authorize emergency claim by owner (user must call this)
   * Allows the contract owner to trigger claims on user's behalf in emergencies
   * @param authorized true to grant permission, false to revoke
   */
  async authorizeEmergencyClaim(
    authorized: boolean
  ): Promise<{ success: boolean; txHash: string }> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract) {
      throw new Error("Web3 not initialized or wallet not connected");
    }

    try {
      const tx = await this.vestingContract.methods
        .authorizeEmergencyClaim(this.currentAccount, authorized)
        .send({ from: this.currentAccount });

      return { success: true, txHash: tx.transactionHash };
    } catch (error) {
      console.error("Emergency authorization error:", error);
      throw error;
    }
  }

  /**
   * Check if emergency claim is authorized for a user
   * Returns true if the owner can trigger claims on behalf of the user
   */
  async isEmergencyClaimAuthorized(userAddress: string): Promise<boolean> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      const ownerAddress = await this.vestingContract.methods.owner().call();
      return await this.vestingContract.methods
        .isEmergencyClaimAuthorized(userAddress, ownerAddress)
        .call();
    } catch (error) {
      console.error("Error checking emergency authorization:", error);
      return false;
    }
  }

  /**
   * Get global vesting parameters that apply to all users by default
   * Returns initial and weekly unlock percentages in basis points
   */
  async getGlobalVestingParams(): Promise<{
    initialPercent: number;
    weeklyPercent: number;
  }> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      const params: any = await this.vestingContract.methods
        .getGlobalVestingParams()
        .call();

      return {
        initialPercent: Number(params[0]),
        weeklyPercent: Number(params[1]),
      };
    } catch (error) {
      console.error("Error getting global params:", error);
      throw error;
    }
  }

  /**
   * Check if a wallet address is an admin
   * Admins can pause/unpause, set parameters, and manage users
   */
  async isAdmin(address: string): Promise<boolean> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      const admin: any = await this.vestingContract.methods
        .isAdmin(address)
        .call();
      return admin;
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  }

  /**
   * Get total number of depositors in the contract
   * Useful for displaying statistics or iterating through users
   */
  async getDepositorCount(): Promise<number> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      const count = await this.vestingContract.methods
        .getDepositorCount()
        .call();
      return Number(count);
    } catch (error) {
      console.error("Error getting depositor count:", error);
      return 0;
    }
  }

  /**
   * Get depositor address by index (0-based)
   * Use with getDepositorCount() to iterate through all depositors
   */
  async getDepositor(index: number): Promise<string> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    try {
      return await this.vestingContract.methods.getDepositor(index).call();
    } catch (error) {
      console.error("Error getting depositor:", error);
      throw error;
    }
  }

  async getDepositedFee(walletAddress: string): Promise<string> {
    if (!this.web3 || !this.vestingContract)
      throw new Error("Web3 not initialized");

    const feeBalanceWei = (await this.vestingContract.methods
      .userFeeDeposits(walletAddress)
      .call()) as string;
    // BNB is 18 decimals.
    return formatUnits(feeBalanceWei.toString(), 18);
  }

  async getVestingInfo(walletAddress: string): Promise<VestingInfo> {
    if (!this.web3 || !this.vestingContract) {
      throw new Error("Web3 not initialized");
    }

    try {
      const result = (await this.vestingContract.methods
        .getVestForUser(walletAddress)
        .call()) as VestingContractResult;

      const convertBNBAmount = (amountInWei: string) => {
        if (!amountInWei) return "0";
        return formatUnits(amountInWei, 18);
      };

      const vestInfoFromContract = {
        depositAmount: result.depositAmount.toString(),
        claimedAmount: result.claimedAmount.toString(),
        startTime: Number(result.startTime),
        initiated: result.initiated,
        claimable: result.claimable.toString(),
        fee: convertBNBAmount(result.fee.toString()),
      };

      const decimalsNum = await this.getDecimals(CVT_V1_TOKEN_ADDRESS!);

      const convertAmount = (amount: string) => {
        if (!amount) return "0";
        return formatUnits(amount, decimalsNum);
      };

      return {
        depositAmount: convertAmount(vestInfoFromContract.depositAmount),
        claimedAmount: convertAmount(vestInfoFromContract.claimedAmount),
        startTime: vestInfoFromContract.startTime,
        initiated: vestInfoFromContract.initiated,
        claimable: convertAmount(vestInfoFromContract.claimable),
        fee: vestInfoFromContract.fee, // Fee is in Wei (BNB), no conversion needed here
      };
    } catch (error) {
      console.error("Detailed error in getVestingInfo:", error);

      // Log the specific error type
      if (error instanceof Error) {
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }

      throw error;
    }
  }

  async hasDeposited(walletAddress: string): Promise<boolean> {
    try {
      const vestingInfo = await this.getVestingInfo(walletAddress);
      return vestingInfo.initiated;
    } catch (error) {
      console.error("Error checking deposit status:", error);
      return false;
    }
  }

  // Helper method to get current network
  async getCurrentNetwork(): Promise<string> {
    if (!this.web3) {
      throw new Error("Web3 not initialized");
    }

    try {
      const networkId = await this.web3.eth.net.getId();
      return networkId.toString();
    } catch (error) {
      console.error("Error getting network:", error);
      throw new Error("Failed to get current network");
    }
  }

  // Helper method to verify network connection
  async verifyNetworkConnection(): Promise<{
    connected: boolean;
    networkId?: string;
    networkName?: string;
  }> {
    if (!this.web3) {
      return { connected: false };
    }

    try {
      const networkId = await this.web3.eth.net.getId();
      const networkMap: { [key: string]: string } = {
        "1": "Ethereum Mainnet",
        "56": "BSC Mainnet",
        "97": "BSC Testnet",
        "137": "Polygon Mainnet",
        "80001": "Mumbai Testnet",
        "43114": "Avalanche Mainnet",
        "43113": "Avalanche Testnet",
      };

      return {
        connected: true,
        networkId: networkId.toString(),
        networkName:
          networkMap[networkId.toString()] || `Unknown Network (${networkId})`,
      };
    } catch (error) {
      console.error("Network verification failed:", error);
      return { connected: false };
    }
  }

  // Add these new methods to your Web3ServiceClass

  async setAutomationBot(botAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .setAutomationBot(botAddress)
      .send({ from: this.currentAccount });
  }

  /**
   * ADMIN: Block a single user from claiming tokens
   * Only admins and owner can call this function
   */
  async blockUser(userAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .blockUser(userAddress)
      .send({ from: this.currentAccount });
  }

  /**
   * ADMIN: Unblock a previously blocked user
   * Only admins and owner can call this function
   */
  async unblockUser(userAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .unblockUser(userAddress)
      .send({ from: this.currentAccount });
  }

  async activateVestingForUser(
    user: string,
    initialPercent: string,
    minutelyPercent: string
  ): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .activateVestingForUser(user, initialPercent, minutelyPercent)
      .send({ from: this.currentAccount });
  }

  /**
   * Percentages are stored on-chain scaled by 100 (100 = 1%), which is how
   * getContractStatus reads them back. The UI works in human percent, so the
   * same scale has to be applied here or a "1" typed by the admin would be
   * written as 0.01%.
   */
  async setGlobalVestingParams(
    initialPercent: string,
    minutelyPercent: string
  ): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");

    const toScaled = (v: string, field: string): string => {
      const n = Number(v);
      if (!v.trim() || !Number.isFinite(n) || n < 0) {
        throw new Error(`${field} must be a non-negative number.`);
      }
      const raw = Math.round(n * 100);
      if (!Number.isInteger(n * 100)) {
        throw new Error(`${field} supports at most 2 decimal places.`);
      }
      return raw.toString();
    };

    await this.vestingContract.methods
      .setGlobalVestingParams(
        toScaled(initialPercent, "Initial percent"),
        toScaled(minutelyPercent, "Weekly percent")
      )
      .send({ from: this.currentAccount });
  }

  /**
   * ADMIN: Set Chainlink Automation upkeep address
   * Only owner can call this function
   */
  async setChainlinkUpkeep(upkeepAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .setChainlinkUpkeep(upkeepAddress)
      .send({ from: this.currentAccount });
  }

  // ---------------------------------------------------------------------
  // Chainlink Automation upkeep monitoring + funding
  // ---------------------------------------------------------------------

  private getRegistryContract(): any {
    if (!this.web3) throw new Error("Web3 not initialized");
    return new this.web3.eth.Contract(
      AutomationRegistryABI as any,
      AUTOMATION_REGISTRY_ADDRESS
    );
  }

  /**
   * Upkeep balance vs the registry's required minimum.
   *
   * Uses getBalance/getMinBalance from IAutomationRegistryConsumer — the
   * interface Chainlink commits to keeping stable across registry upgrades —
   * rather than getUpkeep, so this survives a registry version bump.
   *
   * Returns null when no upkeep id is configured.
   */
  async getUpkeepStatus(): Promise<UpkeepStatus | null> {
    if (!UPKEEP_ID) return null;
    if (!this.web3) {
      await this.init();
      if (!this.web3) throw new Error("Web3 not initialized");
    }

    const chainId = await this.getChainId();
    if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
      throw new WrongNetworkError(chainId);
    }

    const registry = this.getRegistryContract();
    const [balanceRaw, minBalanceRaw] = await Promise.all([
      registry.methods.getBalance(UPKEEP_ID).call(),
      registry.methods.getMinBalance(UPKEEP_ID).call(),
    ]);

    const balance = balanceRaw.toString();
    const minBalance = minBalanceRaw.toString();

    return {
      upkeepId: UPKEEP_ID,
      balanceRaw: balance,
      minBalanceRaw: minBalance,
      balance: formatUnits(balance, 18),
      minBalance: formatUnits(minBalance, 18),
      // LINK is 18 decimals on BNB Chain.
      isUnderfunded: BigInt(balance) < BigInt(minBalance),
    };
  }

  /** Caller's LINK balance, as an exact decimal string. */
  async getLinkBalance(): Promise<string> {
    if (!this.web3 || !this.currentAccount)
      throw new Error("Wallet not connected");
    const link = this.getTokenContract(LINK_TOKEN_ADDRESS);
    const raw = (await link.methods
      .balanceOf(this.currentAccount)
      .call()) as string;
    return formatUnits(raw.toString(), 18);
  }

  private getRouterContract(): any {
    if (!this.web3) throw new Error("Web3 not initialized");
    return new this.web3.eth.Contract(
      PancakeRouterABI as any,
      PANCAKE_ROUTER_ADDRESS
    );
  }

  /**
   * Quote BNB -> Binance-Peg LINK on PancakeSwap.
   *
   * Returns the expected output and the caller's peg-LINK balance. This is
   * only the first leg — peg-LINK still needs PegSwap conversion before it
   * can fund an upkeep.
   */
  async quoteBnbToPegLink(bnbAmount: string): Promise<SwapQuote> {
    if (!this.web3) {
      await this.init();
      if (!this.web3) throw new Error("Web3 not initialized");
    }
    if (!SWAP_SUPPORTED) {
      throw new Error("Swapping is only available on BSC Mainnet.");
    }

    const chainId = await this.getChainId();
    if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
      throw new WrongNetworkError(chainId);
    }

    const amountInRaw = parseUnits(bnbAmount, 18);
    if (BigInt(amountInRaw) <= 0n) {
      throw new Error("Amount must be greater than zero.");
    }

    const router = this.getRouterContract();
    const amounts = (await router.methods
      .getAmountsOut(amountInRaw, [WBNB_ADDRESS, PEG_LINK_ADDRESS])
      .call()) as string[];

    const amountOutRaw = amounts[amounts.length - 1].toString();
    return {
      amountInRaw,
      amountOutRaw,
      amountOut: formatUnits(amountOutRaw, 18),
    };
  }

  /**
   * BNB held by the automation bot wallet, and whether it needs a top-up.
   * Read-only, so it works without a connected wallet.
   */
  async getAutomationWalletStatus(): Promise<AutomationWalletStatus> {
    if (!this.web3) {
      await this.init();
      if (!this.web3) throw new Error("Web3 not initialized");
    }
    const raw = await this.web3.eth.getBalance(AUTOMATION_WALLET);
    const balance = formatUnits(raw.toString(), 18);
    return {
      address: AUTOMATION_WALLET,
      balance,
      isLow: compareDecimals(balance, AUTOMATION_LOW_BNB) < 0,
    };
  }

  /**
   * Send BNB from the connected wallet to the automation bot.
   * amount is a decimal BNB string, e.g. "0.05".
   */
  async fundAutomationWallet(amount: string): Promise<{ txHash: string }> {
    if (!this.web3 || !this.currentAccount)
      throw new Error("Wallet not connected");

    const chainId = await this.getChainId();
    if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
      throw new WrongNetworkError(chainId);
    }

    const valueRaw = parseUnits(amount, 18);
    if (BigInt(valueRaw) <= 0n) {
      throw new Error("Amount must be greater than zero.");
    }

    // Gas comes out of the same balance, so check before prompting the wallet.
    const balance = await this.web3.eth.getBalance(this.currentAccount);
    if (BigInt(balance.toString()) <= BigInt(valueRaw)) {
      throw new Error(
        `Insufficient BNB. You have ${formatUnits(
          balance.toString(),
          18
        )} BNB and need ${amount} plus gas.`
      );
    }

    const receipt = await this.web3.eth.sendTransaction({
      from: this.currentAccount,
      to: AUTOMATION_WALLET,
      value: valueRaw,
    });
    return { txHash: String(receipt.transactionHash) };
  }

  /** Caller's native BNB balance, as an exact decimal string. */
  async getBnbBalance(): Promise<string> {
    if (!this.web3 || !this.currentAccount)
      throw new Error("Wallet not connected");
    const raw = await this.web3.eth.getBalance(this.currentAccount);
    return formatUnits(raw.toString(), 18);
  }

  /** Caller's Binance-Peg LINK balance, as an exact decimal string. */
  async getPegLinkBalance(): Promise<string> {
    if (!this.web3 || !this.currentAccount)
      throw new Error("Wallet not connected");
    const token = this.getTokenContract(PEG_LINK_ADDRESS);
    const raw = (await token.methods
      .balanceOf(this.currentAccount)
      .call()) as string;
    return formatUnits(raw.toString(), 18);
  }

  /**
   * Swap BNB for Binance-Peg LINK on PancakeSwap.
   *
   * slippagePercent guards against the price moving between quote and
   * execution; the swap reverts rather than filling at a worse rate.
   */
  async swapBnbForPegLink(
    bnbAmount: string,
    slippagePercent = 1
  ): Promise<{ success: boolean; amountOut: string }> {
    if (!this.web3 || !this.currentAccount)
      throw new Error("Wallet not connected");
    if (!SWAP_SUPPORTED) {
      throw new Error("Swapping is only available on BSC Mainnet.");
    }

    const chainId = await this.getChainId();
    if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
      throw new WrongNetworkError(chainId);
    }

    const quote = await this.quoteBnbToPegLink(bnbAmount);

    // Fail early with a clear message rather than letting the wallet surface
    // a bare "insufficient funds" — gas still has to come out of the balance.
    const bnbBalance = await this.web3.eth.getBalance(this.currentAccount);
    if (BigInt(bnbBalance.toString()) <= BigInt(quote.amountInRaw)) {
      throw new Error(
        `Insufficient BNB. You have ${formatUnits(
          bnbBalance.toString(),
          18
        )} BNB and need ${bnbAmount} plus gas.`
      );
    }

    const bps = BigInt(Math.round(slippagePercent * 100));
    const amountOutMin = (
      (BigInt(quote.amountOutRaw) * (10000n - bps)) /
      10000n
    ).toString();

    // 10 minutes; long enough for a mobile wallet confirmation, short enough
    // that a stale transaction cannot execute much later at a bad price.
    const deadline = (
      Math.floor(Date.now() / 1000) + 600
    ).toString();

    const swap = this.getRouterContract().methods.swapExactETHForTokens(
      amountOutMin,
      [WBNB_ADDRESS, PEG_LINK_ADDRESS],
      this.currentAccount,
      deadline
    );

    const gas = await swap.estimateGas({
      from: this.currentAccount,
      value: quote.amountInRaw,
    });

    await swap.send({
      from: this.currentAccount,
      value: quote.amountInRaw,
      gas: ((BigInt(gas) * 120n) / 100n).toString(),
    });

    return { success: true, amountOut: quote.amountOut };
  }

  /**
   * The LINK address the registry itself will accept, read from the registry.
   *
   * BNB Chain has two tokens both named "ChainLink Token" with symbol "LINK":
   * the native ERC-677 token (which Automation accepts) and Binance-Peg LINK
   * (which DEXes trade and Automation rejects). Name and symbol are identical,
   * so the only safe discriminator is the address — and the registry is the
   * authority on which one it wants.
   */
  async getRegistryLinkAddress(): Promise<string> {
    const registry = this.getRegistryContract();
    return (await registry.methods.getLinkAddress().call()) as string;
  }

  /**
   * Fund the upkeep with LINK: approve the registry, then addFunds.
   *
   * Anyone can fund an upkeep — the caller does not need to be its admin.
   * amount is a decimal LINK string, e.g. "5.5".
   */
  async fundUpkeep(amount: string): Promise<{ success: boolean }> {
    if (!this.web3 || !this.currentAccount)
      throw new Error("Wallet not connected");
    if (!UPKEEP_ID) throw new Error("No upkeep ID configured.");

    const chainId = await this.getChainId();
    if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
      throw new WrongNetworkError(chainId);
    }

    // Fail loudly if our configured LINK is not the one the registry accepts.
    // Peg-LINK has the same name and symbol, so without this the approve would
    // succeed and addFunds would revert with nothing explaining why.
    const expectedLink = await this.getRegistryLinkAddress();
    if (expectedLink.toLowerCase() !== LINK_TOKEN_ADDRESS.toLowerCase()) {
      throw new Error(
        `Wrong LINK token. The registry expects ${expectedLink}, but the app ` +
          `is configured with ${LINK_TOKEN_ADDRESS}. Automation only accepts ` +
          `native ERC-677 LINK — Binance-Peg LINK must be converted via PegSwap first.`
      );
    }

    const amountRaw = parseUnits(amount, 18);
    if (BigInt(amountRaw) <= 0n) {
      throw new Error("Amount must be greater than zero.");
    }

    const link = this.getTokenContract(LINK_TOKEN_ADDRESS);
    const linkBalance = (await link.methods
      .balanceOf(this.currentAccount)
      .call()) as string;
    if (BigInt(linkBalance.toString()) < BigInt(amountRaw)) {
      throw new Error(
        `Insufficient LINK. You have ${formatUnits(
          linkBalance.toString(),
          18
        )} LINK.`
      );
    }

    // approve() the registry to pull the LINK, then addFunds() moves it.
    const approveGas = await link.methods
      .approve(AUTOMATION_REGISTRY_ADDRESS, amountRaw)
      .estimateGas({ from: this.currentAccount });
    await link.methods
      .approve(AUTOMATION_REGISTRY_ADDRESS, amountRaw)
      .send({
        from: this.currentAccount,
        gas: ((BigInt(approveGas) * 120n) / 100n).toString(),
      });

    const registry = this.getRegistryContract();
    const fundGas = await registry.methods
      .addFunds(UPKEEP_ID, amountRaw)
      .estimateGas({ from: this.currentAccount });
    await registry.methods.addFunds(UPKEEP_ID, amountRaw).send({
      from: this.currentAccount,
      gas: ((BigInt(fundGas) * 120n) / 100n).toString(),
    });

    return { success: true };
  }

  /**
   * ADMIN: Add a new admin to the contract
   * Only owner can call this function
   */
  async addAdmin(adminAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .addAdmin(adminAddress)
      .send({ from: this.currentAccount });
  }

  /**
   * ADMIN: Remove an admin from the contract
   * Only owner can call this function
   */
  async removeAdmin(adminAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .removeAdmin(adminAddress)
      .send({ from: this.currentAccount });
  }

  async pauseContract(): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .pause()
      .send({ from: this.currentAccount });
  }

  async unpauseContract(): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .unpause()
      .send({ from: this.currentAccount });
  }

  /**
   * ADMIN: Withdraw collected fees to owner address
   * The amount should be in BNB (e.g., "0.1" for 0.1 BNB)
   */
  async withdrawFees(): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .withdrawFees()
      .send({ from: this.currentAccount });
  }

  /**
   * ADMIN: Emergency trigger claim for a user (requires user authorization)
   * Only admins can call this, and only if user has authorized emergency claims
   */
  async emergencyTriggerClaim(userAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods.emergencyTriggerClaim(userAddress).send({
      from: this.currentAccount,
      gas: "300000",
    });
  }

  /**
   * ADMIN: Withdraw old CVT tokens that aren't allocated to users
   * Only withdraws excess tokens, not user deposits
   */
  async withdrawOldTokens(): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .withdrawOldTokens()
      .send({ from: this.currentAccount });
  }

  async recoverERC20(tokenAddress: string): Promise<void> {
    if (!this.web3 || !this.currentAccount || !this.vestingContract)
      throw new Error("Wallet not connected");
    await this.vestingContract.methods
      .recoverERC20(tokenAddress)
      .send({ from: this.currentAccount });
  }
}

const Web3Service = new Web3ServiceClass();
export default Web3Service;